import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { incentivesApi } from '../api/incentivesApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM_STATE = {
  emp_id: '',
  emp_name: '',
  date: new Date().toISOString().slice(0, 10),
  pay_head_id: '',
  amount: '',
  vno: null,
};

export function useIncentivesEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const [dynamicOptions, setDynamicOptions] = useState({
    employees: [],
    payHeads: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load dynamic options (active employees and earning payheads)
  const loadOptions = useCallback(async () => {
    try {
      const opts = await incentivesApi.getIncentiveOptions();
      setDynamicOptions(opts);
      return opts;
    } catch (err) {
      console.error('Failed to load incentive options:', err);
      appToast.error('Failed to load options for employee incentives.');
      return { employees: [], payHeads: [] };
    }
  }, []);

  // Load existing incentive record when in edit mode
  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const record = await incentivesApi.getIncentiveById(id);
      if (record) {
        setForm({
          emp_id: record.emp_id || '',
          emp_name: record.employee || '',
          date: record.vDate || new Date().toISOString().slice(0, 10),
          pay_head_id: record.pay_head_id || '',
          amount: record.amount ?? '',
          vno: record.vnoRaw || null,
        });
      } else {
        appToast.error('Incentive record not found.');
        navigate('/payroll/incentives');
      }
    } catch (err) {
      appToast.error('Failed to load incentive details.');
      navigate('/payroll/incentives');
    }
  }, [id, navigate]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    Promise.all([loadOptions(), isEdit ? loadData() : Promise.resolve()])
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [loadOptions, loadData, isEdit]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async ({ exitAfter = true } = {}) => {
    if (!form.emp_id || Number(form.emp_id) === 0) {
      appToast.error('Please Select Employee Name');
      return;
    }
    if (!form.pay_head_id || Number(form.pay_head_id) === 0) {
      appToast.error('Please Select PayHead');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      appToast.error('Please enter a valid amount');
      return;
    }
    if (!form.date) {
      appToast.error('Please select a voucher date.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        emp_id: Number(form.emp_id),
        pay_head_id: Number(form.pay_head_id),
        amount: Number(form.amount),
        v_date: form.date,
      };

      if (isEdit) {
        await incentivesApi.updateIncentive(id, payload);
        appToast.success('Incentive updated successfully!');
      } else {
        await incentivesApi.createIncentive(payload);
        appToast.success('Incentive recorded successfully!');
      }

      if (exitAfter) {
        navigate('/payroll/incentives');
      } else if (!isEdit) {
        setForm({
          ...DEFAULT_FORM_STATE,
          date: form.date, // keep same date for rapid subsequent entries
        });
      }
    } catch (err) {
      const errMsg = err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Failed to save incentive.';
      appToast.error(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      isEdit,
      editId: id,
      isLoading,
      isSaving,
      dynamicOptions,
    },
    actions: {
      patchForm,
      handleSave,
    },
  };
}
